import React, { useState, useRef } from "react";
import { PDFDocument } from "pdf-lib";

interface PDFSplitterProps {
  onRunRecorded?: (input: Record<string, unknown>, output: unknown) => void;
}

function parsePageRange(rangeStr: string, totalPages: number): number[] {
  const pages = new Set<number>();
  const parts = rangeStr.split(",").map((s) => s.trim());
  for (const part of parts) {
    if (part.includes("-")) {
      const [start, end] = part.split("-").map((n) => parseInt(n.trim(), 10));
      if (isNaN(start) || isNaN(end) || start < 1 || end > totalPages || start > end) {
        throw new Error(`Invalid range "${part}". Pages must be between 1 and ${totalPages}.`);
      }
      for (let i = start; i <= end; i++) pages.add(i);
    } else {
      const n = parseInt(part, 10);
      if (isNaN(n) || n < 1 || n > totalPages) {
        throw new Error(`Invalid page number "${part}". Must be between 1 and ${totalPages}.`);
      }
      pages.add(n);
    }
  }
  if (pages.size === 0) throw new Error("No valid pages specified.");
  return Array.from(pages).sort((a, b) => a - b);
}

export default function PDFSplitterTool({ onRunRecorded }: PDFSplitterProps) {
  const [file, setFile] = useState<File | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [pageRange, setPageRange] = useState("");
  const [splitting, setSplitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultPageCount, setResultPageCount] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (f: File | null) => {
    setResultUrl(null);
    setError(null);
    setTotalPages(null);
    if (!f) { setFile(null); return; }
    if (!f.name.endsWith(".pdf") && f.type !== "application/pdf") {
      setError("Please select a PDF file.");
      return;
    }
    setFile(f);
    try {
      const bytes = await f.arrayBuffer();
      const pdf = await PDFDocument.load(bytes);
      setTotalPages(pdf.getPageCount());
    } catch {
      setError("Could not read PDF. Make sure it is a valid PDF file.");
      setFile(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0] ?? null);
  };

  const split = async () => {
    if (!file || totalPages === null) return;
    setError(null);
    setSplitting(true);
    setResultUrl(null);
    try {
      let pages: number[];
      const trimmed = pageRange.trim();
      if (!trimmed) {
        // No range = all pages (passthrough)
        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
      } else {
        pages = parsePageRange(trimmed, totalPages);
      }

      const bytes = await file.arrayBuffer();
      const srcPdf = await PDFDocument.load(bytes);
      const newPdf = await PDFDocument.create();
      const indices = pages.map((p) => p - 1); // 0-based
      const copied = await newPdf.copyPages(srcPdf, indices);
      copied.forEach((p) => newPdf.addPage(p));

      const resultBytes = await newPdf.save();
      const blob = new Blob([resultBytes as unknown as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setResultUrl(url);
      setResultPageCount(newPdf.getPageCount());
      onRunRecorded?.(
        { fileName: file.name, pageRange: trimmed || "all", totalPages },
        { extractedPages: pages.length, sizeBytes: resultBytes.byteLength },
      );
    } catch (err: any) {
      setError(err?.message || "PDF split failed.");
    } finally {
      setSplitting(false);
    }
  };

  const fileName = file
    ? `split-${pageRange.replace(/[^0-9,\-]/g, "") || "all"}.pdf`
    : "split.pdf";

  return (
    <div className="space-y-6">
      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${file ? "border-indigo-400 bg-indigo-50" : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />
        {file ? (
          <div>
            <p className="font-medium text-indigo-700">{file.name}</p>
            {totalPages !== null && <p className="text-sm text-gray-500 mt-1">{totalPages} pages · {(file.size / 1024).toFixed(1)} KB</p>}
          </div>
        ) : (
          <div>
            <p className="text-gray-500 text-sm">Drop a PDF here or click to browse</p>
          </div>
        )}
      </div>

      {/* Page range input */}
      {totalPages !== null && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pages to extract <span className="text-gray-400 font-normal">(e.g. 1-3, 5, 7-9 — leave blank for all)</span>
          </label>
          <input
            type="text"
            placeholder={`1-${totalPages}`}
            value={pageRange}
            onChange={(e) => setPageRange(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
          />
          <p className="text-xs text-gray-400 mt-1">Source PDF has {totalPages} pages.</p>
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <button
        onClick={split}
        disabled={!file || splitting}
        className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition"
      >
        {splitting ? "Splitting..." : "Extract Pages"}
      </button>

      {resultUrl && resultPageCount !== null && (
        <div className="border rounded-xl p-5 bg-green-50 text-center">
          <p className="font-semibold text-green-800 mb-1">Done! {resultPageCount} page{resultPageCount !== 1 ? "s" : ""} extracted.</p>
          <a
            href={resultUrl}
            download={fileName}
            className="inline-block mt-2 px-6 py-2 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 text-sm"
          >
            Download PDF
          </a>
        </div>
      )}
    </div>
  );
}
